import React from 'react';
import {
  DownloadOutlined,
  ExportOutlined,
  EyeOutlined,
  FileTextOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Rate, Tag, Typography } from 'antd';
import { JobApplication } from '../models/job-application.model';
import { formatApplicationSkillScore } from '../utils/application-relevance-labels';
import { jo } from '../../../../../theme/css-vars';

interface CandidateSidebarProps {
  application: JobApplication;
  onViewCandidate: () => void;
  onDownloadResume: () => void;
  onDownloadPackage: () => void;
}

function formatCurrency(currency: string | null | undefined): string {
  if (!currency) return '$';
  switch (currency) {
    case 'CRC_COLON':
      return '₡';
    case 'US_DOLLAR':
      return '$';
    default:
      return currency;
  }
}

function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const CandidateSidebar: React.FC<CandidateSidebarProps> = ({
  application,
  onViewCandidate,
  onDownloadResume,
  onDownloadPackage,
}) => {
  const skillScore = formatApplicationSkillScore(application.relevance);
  const salaryCurrency = formatCurrency(application.currency || application.job?.currency);
  const phoneDigits = (application.phoneNumber || '').replace(/\D/g, '');
  const expectedSalary = application.expectedSalary
    ? `${salaryCurrency}${application.expectedSalary.toLocaleString()}`
    : 'Not specified';

  const sendEmail = () => {
    if (!application.email) return;
    window.location.href = `mailto:${application.email}`;
  };

  const openWhatsApp = () => {
    if (!phoneDigits) return;
    window.open(`https://wa.me/${phoneDigits}`, '_blank', 'noopener,noreferrer');
  };

  const openProfileUrl = () => {
    if (!application.websiteURL) return;
    window.open(application.websiteURL, '_blank', 'noopener,noreferrer');
  };

  return (
    <aside className="application-panel candidate-sidebar" aria-label="Candidate profile">
      <div className="candidate-sidebar__identity">
        <Avatar
          size={52}
          style={{ backgroundColor: jo.primary, flexShrink: 0, fontWeight: 700 }}
          aria-hidden
        >
          {initials(application.fullname)}
        </Avatar>
        <div className="min-w-0 flex-1">
          <Typography.Title level={4} className="!mb-1 !mt-0 truncate !text-base">
            {application.fullname || 'Candidate'}
          </Typography.Title>
          <Typography.Text type="secondary" className="block truncate text-xs">
            {application.job?.name || 'No job assigned'}
          </Typography.Text>
          {application.job?.client?.name ? (
            <Typography.Text type="secondary" className="block truncate text-xs">
              {application.job.client.name}
            </Typography.Text>
          ) : null}
        </div>
      </div>

      <div className="candidate-sidebar__match">
        <div>
          <Typography.Text type="secondary" className="block text-xs">
            Candidate rating
          </Typography.Text>
          {application.rate != null && application.rate > 0 ? (
            <Rate disabled allowClear={false} value={application.rate} className="text-sm" />
          ) : (
            <Typography.Text type="secondary" className="text-xs">
              Not rated yet
            </Typography.Text>
          )}
        </div>
        {skillScore ? (
          <Tag color="blue" className="m-0">
            {skillScore}
          </Tag>
        ) : null}
      </div>

      <section aria-labelledby="candidate-contact-heading">
        <Typography.Title
          id="candidate-contact-heading"
          level={5}
          className="application-panel__section-title"
        >
          Contact
        </Typography.Title>
        <dl className="candidate-details">
          <div className="candidate-details__row">
            <MailOutlined aria-hidden />
            <div className="min-w-0">
              <dt>Email</dt>
              <dd>
                <Typography.Text
                  ellipsis={{ tooltip: application.email }}
                  copyable={application.email ? { text: application.email } : false}
                >
                  {application.email || 'Not provided'}
                </Typography.Text>
              </dd>
            </div>
          </div>
          <div className="candidate-details__row">
            <PhoneOutlined aria-hidden />
            <div className="min-w-0">
              <dt>Phone</dt>
              <dd>
                <Typography.Text
                  ellipsis={{ tooltip: application.phoneNumber }}
                  copyable={
                    application.phoneNumber ? { text: application.phoneNumber } : false
                  }
                >
                  {application.phoneNumber || 'Not provided'}
                </Typography.Text>
              </dd>
            </div>
          </div>
        </dl>
      </section>

      <div className="candidate-contact-actions" aria-label="Candidate contact actions">
        <Button
          icon={<MailOutlined />}
          onClick={sendEmail}
          disabled={!application.email}
        >
          Send email
        </Button>
        <Button
          icon={<WhatsAppOutlined />}
          onClick={openWhatsApp}
          disabled={!phoneDigits}
        >
          WhatsApp
        </Button>
      </div>

      <section aria-labelledby="candidate-files-heading">
        <Typography.Title
          id="candidate-files-heading"
          level={5}
          className="application-panel__section-title"
        >
          Application
        </Typography.Title>
        <dl className="candidate-details">
          <div className="candidate-details__row">
            <span className="candidate-details__currency" aria-hidden>
              {salaryCurrency}
            </span>
            <div>
              <dt>Expected salary</dt>
              <dd>{expectedSalary}</dd>
            </div>
          </div>
          <div className="candidate-details__row">
            <FileTextOutlined aria-hidden />
            <div className="min-w-0">
              <dt>Resume</dt>
              <dd className="truncate" title={application.file?.name}>
                {application.file?.name || 'Not uploaded'}
              </dd>
            </div>
          </div>
        </dl>
      </section>

      <div className="candidate-sidebar__primary-actions">
        <Button
          type="primary"
          block
          icon={<DownloadOutlined />}
          onClick={onDownloadResume}
          disabled={!application.file?.id}
        >
          Download resume
        </Button>
        <Button
          block
          icon={<EyeOutlined />}
          onClick={onViewCandidate}
          disabled={!application.fullname}
        >
          View candidate
        </Button>
      </div>

      <div className="candidate-sidebar__secondary-actions">
        <Button
          type="text"
          icon={<LinkOutlined />}
          onClick={openProfileUrl}
          disabled={!application.websiteURL}
        >
          Open profile
        </Button>
        <Button
          type="text"
          icon={<ExportOutlined />}
          onClick={onDownloadPackage}
          disabled={!application.id || !application.job?.id}
        >
          Export package
        </Button>
      </div>
    </aside>
  );
};

export default CandidateSidebar;
